import { withNamespaces } from "react-i18next";
import React, { useState, memo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import Spinner from "react-bootstrap/Spinner";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";

import { toast } from "react-toastify";

import DateUtils from "src/services/utils/DateUtils";

import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT } from "src/common/constants";

import {
  Button,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
} from "reactstrap";

import {
  HistoryLogActions,
  HistoryLogRoles,
} from "src/modules/data-protection/constants";

import { withRouter } from "react-router-dom";
import FilteringComponent from "../../../../../components/history-log/filtering-component";
import DataProtectionTomsService from "src/modules/data-protection/apis/DataProtectionTomsService";
import DetailsModal from "../../../../../components/history-log/details-modal";
import ExportExcelButton from "./components/export-button";

const HistoryLog = ({ t, tomId }) => {
  const [tableFilters, setTableFilters] = useState({
    pageIndex: 1,
    pageSize: 25,
  });

  const [moreActionsDropDownOpen, setMoreActionsDropDownOpen] = useState(false);

  const toggleMoreActionsDropDown = () =>
    setMoreActionsDropDownOpen(!moreActionsDropDownOpen);

  const [selectedLog, setSelectedLog] = useState(null);

  const dateUtils = new DateUtils();

  const tableColumns = [
    {
      dataField: "secondary_id",
      text: "#",
      sort: false,
      key: 1,
      style: {
        width: 120,
      },
    },
    {
      dataField: "date_time",
      text: t("Date and Time"),
      sort: false,
      key: 3,
      style: {
        width: 180,
      },
      formatter: (cellContent, row) => {
        return (
          <div>
            <span>
              {dateUtils.convertDateToDate(
                row.created_at,
                INTERNATIONAL_DATE_FORMAT
              )}
            </span>
            <br />
            <span className="text-muted">
              {dateUtils.convertDateToDate(row.created_at, "HH:mm")}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "user_name",
      text: t("User"),
      sort: false,
      key: 4,
      style: {
        width: 180,
      },
    },
    {
      dataField: "user_role",
      text: t("User Role"),
      sort: false,
      key: 5,
      style: {
        width: 180,
      },
      formatter: (cellContent, row) => {
        return t(HistoryLogRoles[row.user_role]);
      },
    },
    {
      dataField: "description",
      text: t("Description"),
      sort: false,
      key: 6,
      formatter: (cellContent, row) => {
        const label = HistoryLogActions[row?.section]?.[row?.action];

        return t(label || "");
      },
    },
    {
      dataField: "actions",
      text: t("Action"),
      sort: false,
      key: 7,
      formatter: (cellContent, row) => {
        return (
          <div className="text-danger text-truncate text-capitalize text-center d-flex justify-content-start">
            <Button
              color="primary"
              onClick={() => setSelectedLog(row)}
              outline
              className="border-0"
            >
              <i className="ri-eye-line font-size-20"></i>
            </Button>
          </div>
        );
      },
    },
  ];

  const NoDataIndication = () =>
    handleFetchHistoryLogQuery.isFetched &&
    !handleFetchHistoryLogQuery.length ? (
      <div className="alert m-0" role="alert">
        <p
          style={{
            textAlign: "center",
            marginBottom: 0,
          }}
        >
          {t(EMPTY_LIST)}
        </p>
      </div>
    ) : (
      <></>
    );

  const handleFetchHistoryLogQuery = useQuery({
    queryKey: ["data-protection-tom-history-logs", tomId, tableFilters],
    queryFn: async () => {
      const service = DataProtectionTomsService.getInstance();
      return await service.fetchLogs(tableFilters, tomId);
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: () => {
      toast(t("Failed to fetch history logs."), {
        type: "error",
      });
    },
  });

  const handleTableChange = (type, { page, sizePerPage }) => {
    switch (type) {
      case "pagination":
        setTableFilters({
          ...tableFilters,
          pageIndex: page,
          pageSize: sizePerPage,
        });
        break;
      default:
        return false;
    }
  };

  const handleFilterChange = useCallback((newFilters) => {
    setTableFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
  }, []);

  return (
    <Row className="p-4">
      <Col sm="12">
        <PaginationProvider
          pagination={paginationFactory({
            custom: true,
            page: tableFilters.pageIndex,
            sizePerPage: tableFilters.pageSize,
            totalSize: handleFetchHistoryLogQuery.data?.itemsCount || 0,
            withFirstAndLast: false,
            alwaysShowAllBtns: true,
            prePageText: (
              <span>
                <i className="ri-arrow-left-s-line"></i> {t("Back")}
              </span>
            ),
            nextPageText: (
              <span>
                {t("Next")} <i className="ri-arrow-right-s-line"></i>
              </span>
            ),
            prePageTitle: t("Pre page"),
            firstPageTitle: t("Next page"),
            showTotal: true,
            hideSizePerPage: false,
            sizePerPageList: [
              {
                text: "25",
                value: 25,
              },
              {
                text: "50",
                value: 50,
              },
            ],
          })}
        >
          {({ paginationProps, paginationTableProps }) => {
            return (
              <React.Fragment>
                <Row className="mb-4">
                  <Col
                    sm="12"
                    md="12"
                    lg="12"
                    className="mb-3 d-flex justify-content-end"
                  >
                    <div className="d-flex">
                      <FilteringComponent
                        fixedSection="tom"
                        onChange={handleFilterChange}
                      />

                      <Dropdown
                        isOpen={moreActionsDropDownOpen}
                        toggle={toggleMoreActionsDropDown}
                      >
                        <DropdownToggle
                          caret
                          variant="outline-secondary"
                          outline
                        >
                          {t("Actions")}
                        </DropdownToggle>
                        <DropdownMenu className="dropdown-menu-end">
                          <DropdownItem
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <ExportExcelButton
                              filters={tableFilters}
                              tomId={tomId}
                            />
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col sm="12">
                    <BootstrapTable
                      remote={{
                        pagination: true,
                        filter: false,
                        sort: false,
                        cellEdit: false,
                        search: false,
                      }}
                      loading={
                        handleFetchHistoryLogQuery.isFetching ||
                        handleFetchHistoryLogQuery.isLoading
                      }
                      overlay={overlayFactory({
                        spinner: (
                          <Spinner
                            animation="border"
                            variant="primary"
                            size="md"
                          />
                        ),
                        text: "Loading...",
                      })}
                      onTableChange={handleTableChange}
                      defaultSorted={[]}
                      keyField={"id"}
                      responsive
                      bordered={false}
                      data={handleFetchHistoryLogQuery.data?.logs || []}
                      striped={false}
                      columns={tableColumns}
                      wrapperClasses="table-responsive"
                      classes={"table tpdd-table"}
                      headerWrapperClasses={"thead-light"}
                      style={{
                        overflowX: "auto",
                      }}
                      noDataIndication={() => <NoDataIndication />}
                      {...paginationTableProps}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col sm="12" md="6">
                    <div className="tpdd-pagination-style-1">
                      <PaginationListStandalone {...paginationProps} />

                      <SizePerPageDropdownStandalone {...paginationProps} />
                    </div>
                  </Col>
                </Row>
              </React.Fragment>
            );
          }}
        </PaginationProvider>

        {selectedLog && (
          <DetailsModal
            logId={selectedLog.id}
            onClose={() => setSelectedLog(null)}
          />
        )}
      </Col>
    </Row>
  );
};

export default withNamespaces()(withRouter(memo(HistoryLog)));
